import type { Appointment, WorkOrder, WorkPhase } from "@myapp/contract";
import { AttachmentsCard, ReadonlyField, StatusHeaderCard } from "../../../shared/ui";
import { WorkSessionTimes } from "../../../features/work-session";
import { formatDateTime, getSchadensbeschreibung } from "../lib";
import { AppointmentAddressCard } from "./AppointmentAddressCard";
import { AppointmentMieterCard } from "./AppointmentMieterCard";
import { PreviousAppointmentsList } from "./PreviousAppointmentsList";

type Props = {
  appointment: Appointment;
  ext: WorkOrder["external"] | undefined;
  mapsQuery: string | null | undefined;
  phase: WorkPhase | null;
  statusLabel: string;
  statusColor: string;
  previousAppointments: Appointment[];
  onDiscardTravel?: () => void;
};

/**
 * The whole read-only detail body: status + Behebungstermin, Zeiten, Adresse, Mieter, Gewerk,
 * Schadensbeschreibung, Beschreibung, previous appointments and attachments — all read-only cards,
 * no disabled `Input`s. Status and `Zeiten` are always visible; nothing on this screen collapses.
 */
export const AppointmentDetailFields = ({
  appointment,
  ext,
  mapsQuery,
  phase,
  statusLabel,
  statusColor,
  previousAppointments,
  onDiscardTravel,
}: Props) => {
  // Only known once the appointment has siblings in the same work-order series.
  const totalInSeries = previousAppointments.length > 0 ? previousAppointments.length + 1 : undefined;

  return (
    <>
      <StatusHeaderCard
        statusLabel={statusLabel}
        statusColor={statusColor}
        primaryLabel="Behebungstermin"
        primaryValue={formatDateTime(appointment.schedule.plannedWorkStartsAt)}
        trailing={
          totalInSeries !== undefined && totalInSeries > 1 ? (
            <span className="text-sm text-slate-500">
              Termin {appointment.sequenceNumber} / {totalInSeries}
            </span>
          ) : undefined
        }
      />

      <WorkSessionTimes
        schedule={appointment.schedule}
        phase={phase}
        onDiscardTravel={onDiscardTravel}
      />

      <AppointmentAddressCard address={ext?.hauseingang ?? ""} mapsQuery={mapsQuery} />

      <AppointmentMieterCard mieter={ext?.mieter} />

      <ReadonlyField label="Gewerk" value={ext?.gewerkeText ?? undefined} />

      <ReadonlyField
        label="Schadensbeschreibung"
        value={getSchadensbeschreibung(ext)}
        clampLines={4}
      />

      {appointment.description && (
        <ReadonlyField label="Beschreibung" value={appointment.description} clampLines={4} />
      )}

      {previousAppointments.length > 0 && (
        <PreviousAppointmentsList appointments={previousAppointments} />
      )}

      <AttachmentsCard
        appAttachments={appointment.attachments}
        immotionAttachments={ext?.immotionAttachments}
      />
    </>
  );
};
